import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";

type PageTitleOverrideContextType = {
    override: string | null;
    setOverride: (title: string | null) => void;
};

const PageTitleOverrideContext = createContext<PageTitleOverrideContextType | undefined>(undefined);

export function PageTitleOverrideProvider({ children }: { children: ReactNode }) {
    const [override, setOverride] = useState<string | null>(null);

    const value = useMemo(
        () => ({
            override,
            setOverride,
        }),
        [override]
    );

    return <PageTitleOverrideContext.Provider value={value}>{children}</PageTitleOverrideContext.Provider>;
}

function usePageTitleOverrideContext() {
    const context = useContext(PageTitleOverrideContext);
    if (context === undefined) {
        throw new Error("usePageTitleOverride must be used within a PageTitleOverrideProvider");
    }
    return context;
}

export function usePageTitleOverride(title?: string) {
    const { setOverride } = usePageTitleOverrideContext();

    useEffect(() => {
        setOverride(title ?? null);
        return () => setOverride(null);
    }, [title, setOverride]);
}

export function usePageTitleOverrideValue() {
    const { override } = usePageTitleOverrideContext();
    return override;
}
